import { useState } from 'react'
import { Button, Description, Dropdown, Header, Input, Label, Popover, Switch, TextField } from '@heroui/react'
import { ApiError, api } from '../lib/api'
import { explain } from '../lib/staffErrors'
import type { ReportView, ViewDraft } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { ActionMenu, ReportModal, type MenuEntry } from './kit'

export type ToastFn = (text: string, kind?: 'error') => void

type Asking = 'rename' | 'delete' | 'unshare'

/**
 * The saved views of one kind: a menu of the organisation's shared ones, the
 * reader's own and, for whoever looks after people, those of former staff; Save;
 * and for the view on screen what its reader may do to it (Share, Rename, Delete).
 */
export function SavedViews({
  views,
  current,
  draft,
  canSave,
  onApply,
  onList,
  onReload,
  onSchedules,
  toast,
}: {
  views: ReportView[]
  /** The saved view the screen is showing, if it is one. */
  current?: ReportView
  /** What Save keeps: the screen as it is now. */
  draft: (name: string, shared: boolean) => ViewDraft
  canSave: boolean
  onApply: (v: ReportView) => void
  /** The list after a change made here. */
  onList: (next: ReportView[]) => void
  /** Read the list again: a change refused because the view moved on or went. */
  onReload: () => void
  /** Opens the reports by email, where a schedule that sends a view is stopped. */
  onSchedules?: () => void
  toast: ToastFn
}) {
  const { lang, say } = useStaffText()
  const [saving, setSaving] = useState(false)
  const [asking, setAsking] = useState<Asking | null>(null)
  const close = () => {
    setSaving(false)
    setAsking(null)
  }
  const failed = (err: unknown) => {
    toast(explain(err, lang, 'common.unreachable'), 'error')
    if (err instanceof ApiError && (err.status === 404 || err.status === 409)) onReload()
  }

  const save = async (name: string, share: boolean) => {
    try {
      const { view } = await api.saveReportView(draft(name, share))
      onList([...views.filter((x) => x.id !== view.id), view])
      close()
      toast(say('insight.viewSaved'))
    } catch (err) {
      failed(err)
    }
  }
  const change = async (v: ReportView, next: { name?: string; shared?: boolean }) => {
    try {
      const { view } = await api.updateReportView(v.id, { name: next.name ?? v.name, shared: next.shared ?? v.shared, updatedAt: v.updatedAt })
      onList(views.map((x) => (x.id === v.id ? view : x)))
      close()
      toast(say('insight.viewSaved'))
    } catch (err) {
      close()
      failed(err)
    }
  }
  const takeOver = async (v: ReportView) => {
    try {
      const { view } = await api.takeOverReportView(v.id)
      onList(views.map((x) => (x.id === v.id ? view : x)))
      toast(say('insight.viewTakenOver'))
    } catch (err) {
      failed(err)
    }
  }
  const remove = async (v: ReportView) => {
    try {
      await api.deleteReportView(v.id)
      onList(views.filter((x) => x.id !== v.id))
      close()
      toast(say('insight.viewDeleted'))
    } catch (err) {
      close()
      failed(err)
    }
  }

  const actions: MenuEntry[] = current
    ? [
        ...(current.mine
          ? [
              {
                id: 'share',
                label: say('insight.shareView'),
                icon: current.shared ? ('check' as const) : undefined,
                // Someone else's report by email stops when the view is no longer theirs to see: asked first.
                run: current.shared && current.inUseByOthers > 0 ? () => setAsking('unshare') : () => void change(current, { shared: !current.shared }),
              },
              { id: 'rename', label: say('insight.renameView'), run: () => setAsking('rename') },
            ]
          : []),
        ...(current.mayTakeOver ? [{ id: 'take', label: say('insight.takeOverView'), run: () => void takeOver(current) }] : []),
        ...(current.mayDelete ? [{ id: 'delete', label: say('common.delete'), danger: true, run: () => setAsking('delete') }] : []),
      ]
    : []

  const groups: [string, string, ReportView[]][] = [
    ['shared', say('insight.viewsShared'), views.filter((v) => v.shared && !v.mine && v.ownerId)],
    ['mine', say('insight.viewsMine'), views.filter((v) => v.mine)],
    ['former', say('insight.viewsFormer'), views.filter((v) => !v.mine && !v.ownerId)],
  ]

  return (
    <>
      {views.length > 0 && (
        <span className="rp-viewpick" data-on={current ? '' : undefined}>
          <Dropdown>
            <Button size="sm" variant="secondary" className="rp-viewpick__btn">
              <span className="rp-viewpick__name">{current?.name ?? say('insight.views')}</span>
              <Icon name="chevron" size={12} />
            </Button>
            <Dropdown.Popover placement="bottom start" className="rp-menu">
              <Dropdown.Menu
                aria-label={say('insight.views')}
                selectionMode="single"
                selectedKeys={current ? [current.id] : []}
                onSelectionChange={(keys) => {
                  const id = keys === 'all' ? undefined : [...keys][0]
                  const v = views.find((x) => x.id === id) ?? (id === undefined ? current : undefined)
                  if (v) onApply(v)
                }}
              >
                {groups
                  .filter(([, , list]) => list.length > 0)
                  .map(([key, title, list]) => (
                    <Dropdown.Section key={key} aria-label={title}>
                      <Header>{title}</Header>
                      {list.map((v) => (
                        <Dropdown.Item key={v.id} id={v.id} textValue={v.name}>
                          <Dropdown.ItemIndicator />
                          <span className="rp-menu__text">
                            <Label>{v.name}</Label>
                            {!v.mine && v.ownerName && <Description>{v.ownerName}</Description>}
                          </span>
                          {v.mine && v.shared && <Icon name="people" size={14} />}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Section>
                  ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
          {current && actions.length > 0 && <ActionMenu label={say('insight.options')} icon="more" iconOnly variant="ghost" alignStart items={actions} />}
        </span>
      )}

      <Popover isOpen={saving} onOpenChange={setSaving}>
        <Button size="sm" variant="tertiary" isDisabled={!canSave}>
          <Icon name="plus" size={14} /> {say('insight.saveView')}
        </Button>
        <Popover.Content placement="bottom start" className="rp-pop">
          <Popover.Dialog aria-label={say('insight.saveView')}>
            {saving && <NameForm share onSave={(name, share) => void save(name, share)} />}
          </Popover.Dialog>
        </Popover.Content>
      </Popover>

      {asking === 'rename' && current && (
        <ReportModal title={say('insight.renameView')} size="sm" onClose={close}>
          <NameForm initial={current.name} onSave={(name) => void change(current, { name })} />
        </ReportModal>
      )}
      {asking === 'unshare' && current && (
        <Confirm
          name={current.name}
          note={say('insight.sentByN', { n: current.inUseByOthers })}
          yes={say('insight.stopSharing')}
          onYes={() => void change(current, { shared: false })}
          onNo={close}
        />
      )}
      {asking === 'delete' &&
        current &&
        (current.inUse > 0 ? (
          // The server refuses while a schedule sends it: say why, and where to stop it.
          <Confirm
            name={current.name}
            note={`${say('insight.sentByN', { n: current.inUse })}. ${say('insight.stopThemFirst')}`}
            yes={onSchedules ? say('reportMail.title') : undefined}
            danger={false}
            onYes={() => {
              close()
              onSchedules?.()
            }}
            onNo={close}
          />
        ) : (
          <Confirm name={current.name} yes={say('common.delete')} onYes={() => void remove(current)} onNo={close} />
        ))}
    </>
  )
}

function Confirm({
  name,
  note,
  yes,
  danger = true,
  onYes,
  onNo,
}: {
  name: string
  note?: string
  /** The action; none offers only Cancel. */
  yes?: string
  danger?: boolean
  onYes: () => void
  onNo: () => void
}) {
  const { say } = useStaffText()
  return (
    <ReportModal
      title={name}
      size="sm"
      onClose={onNo}
      footer={() => (
        <>
          <Button variant="tertiary" size="sm" onPress={onNo}>
            {say('common.cancel')}
          </Button>
          {yes && (
            <Button variant={danger ? 'danger' : 'secondary'} size="sm" onPress={onYes}>
              {yes}
            </Button>
          )}
        </>
      )}
    >
      {note && <p className="rp-muted">{note}</p>}
    </ReportModal>
  )
}

/** A view's name, and on Save whether everyone sees it. */
function NameForm({ initial = '', share, onSave }: { initial?: string; share?: boolean; onSave: (name: string, shared: boolean) => void }) {
  const { say } = useStaffText()
  const [name, setName] = useState(initial)
  const [shared, setShared] = useState(false)
  const ok = name.trim() !== '' && name.trim() !== initial
  return (
    <form
      className="rp-nameform"
      onSubmit={(e) => {
        e.preventDefault()
        if (ok) onSave(name.trim(), shared)
      }}
    >
      <TextField autoFocus aria-label={say('insight.viewName')} value={name} maxLength={60} onChange={setName}>
        <Input placeholder={say('insight.viewName')} />
      </TextField>
      {share && (
        <Switch className="rp-switch" isSelected={shared} onChange={setShared}>
          <Switch.Content className="rp-switch__row">
            <Label>{say('insight.shareView')}</Label>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Content>
        </Switch>
      )}
      <Button type="submit" variant="primary" size="sm" isDisabled={!ok}>
        {say('common.save')}
      </Button>
    </form>
  )
}
